import { CircularProgress } from '@mui/material'
import mixpanel from 'mixpanel-browser'
import React, { Dispatch, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import Modal from 'react-modal'
import { useDispatch } from 'react-redux'
import { CheckBox } from 'src/components/global/CheckBox/CheckBox'
import { FormFieldInput } from 'src/components/global/ListingForm/FormFieldInput/FormFieldInput'
import FormFieldLabel from 'src/components/global/ListingForm/FormFieldLabel/FormFieldLabel'
import { ModalCloseButton } from 'src/components/global/ModalCloseButton'
import { StarRating } from 'src/components/global/StarRating'
import { MIXPANEL_ENABLED, RECAPTCHA_ENABLED } from 'src/config/config'
import {
  IEnum_Review_Status,
  IEnum_Solution_Type,
  IReviewInput,
  useSubmitReviewMutation,
} from 'src/graphql/generated/hooks'
import { getIPAddress } from 'src/services/common/getIPAddress'
import { showToast } from 'src/store/actions/toastAction'

interface SubmitReviewModalProps {
  isOpen: boolean
  vendorName: string
  solutionType: IEnum_Solution_Type
  isEnhanced: boolean
  id: string
  onClose: () => void
}

const ModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    padding: '0px',
    transform: 'translate(-50%, -50%)',
    borderWidth: '0px',
    backgroundColor: 'transparent',
    border: '0px solid red',
  },
}

export const SubmitReviewModal: React.FC<SubmitReviewModalProps> = ({
  isOpen,
  vendorName,
  solutionType,
  isEnhanced,
  id,
  onClose,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [rating, setRating] = useState(0)
  const [isProgressing, setIsProgressing] = useState(false)
  const [isAnonymous, setisAnonymous] = useState(false)

  const { executeRecaptcha } = useGoogleReCaptcha()
  const [submitReview] = useSubmitReviewMutation()

  const handleClose = () => {
    setTitle('')
    setNote('')
    setRating(0)
    onClose()
  }

  const onAnonymousChanged = (checked: boolean) => {
    setisAnonymous(checked)
  }

  const submit = async (gReCaptchaToken?: string) => {
    if (note === '' || title === '') {
      dispatch(showToast('Please fill fields', 'warning'))
      return
    }

    if (note.length < 10) {
      dispatch(showToast('Please fill 10 characters more.', 'warning'))
      return
    }
    setIsProgressing(true)
    const ip = await getIPAddress()
    const req: IReviewInput = {
      content: note,
      ip,
      rating,
      status: IEnum_Review_Status.Submitted,
      title,
      anonymous: isAnonymous,
      gReCaptchaToken,
      solution: id,
    }

    const res = await submitReview({ variables: { data: req } })

    if (res) {
      setTitle('')
      setNote('')
      setRating(0)
      onClose()
      dispatch(showToast('Thank you! Your review has been submitted and is being evaluated.', 'success'))
      if (MIXPANEL_ENABLED) {
        mixpanel.track('submit_review_success', {
          solutionType,
          id,
          displayName: vendorName,
          isEnhanced,
        })
      }
    } else {
      dispatch(showToast('Fialed to submit Review', 'error'))
      if (MIXPANEL_ENABLED) {
        mixpanel.track('submit_review_failed', {
          solutionType,
          id,
          displayName: vendorName,
          isEnhanced,
        })
      }
    }

    setTimeout(() => {
      setIsProgressing(false)
    }, 1000)
  }

  const handleSaveChanges = async () => {
    if (RECAPTCHA_ENABLED) {
      if (!executeRecaptcha) {
        dispatch(showToast('Execute recaptcha not yet available', 'error'))
        return
      }

      executeRecaptcha('enquiryFormSubmit').then(async (gReCaptchaToken) => {
        await submit(gReCaptchaToken)
      })

      return
    }

    await submit()
  }
  return (
    <Modal closeTimeoutMS={200} isOpen={isOpen} onRequestClose={handleClose} style={ModalStyles} ariaHideApp={false}>
      <div className="flex">
        <div className="flex flex-col md:w-[600px] w-full md:pl-0 md:pr-0 pl-[30px] pr-[30px]">
          <div className="flex flex-row w-full" style={{ zIndex: '10' }}>
            <ModalCloseButton onClose={handleClose} />
          </div>
          <div className="flex flex-col overflow-y-hidden mt-[-20px] mr-[15px] relative">
            <div className="request-contact-container flex flex-row w-full pl-[60px] pr-[60px]">
              <div className="flex flex-col w-full">
                <div className="flex items-center">
                  <p className="flex items-center h-[81px] text-[24px] font-bold font-secondary text-darkgray">
                    Write a Review
                  </p>
                </div>

                <hr className="bg-rockBlue" />
                <div className="text-darkgray font-primary md:text-[16px] text-[14px] leading-[19px] mt-[11px]">
                  You are leaving a review for{' '}
                  <span className="text-darkgray font-primary text-[16px] leading-[19px] font-bold">{vendorName}</span>.
                  Effective reviews explain how the solution addressed a particular use case, comment on how intuitive
                  the solution is for users, and refer to customer support.
                </div>

                <div className="flex flex-col mt-[35px]">
                  <div className="w-full text-[0.875rem] font-primary font-regular text-darkgray mr-[1.25rem]">
                    <FormFieldLabel label="Overall Rating *" />
                    <div className="flex flex-row h-[48px] items-center">
                      <StarRating value={rating} type={1} precision={1} readOnly={false} setRating={setRating} />
                    </div>
                  </div>

                  <div className="w-full text-[0.875rem] font-primary font-regular text-darkgray mr-[1.25rem]">
                    <FormFieldLabel label="Review Title *" />
                    <FormFieldInput
                      id="title"
                      className="text-[15px] font-primary"
                      type="text"
                      required={true}
                      value={title || ''}
                      onChange={setTitle}
                    />
                  </div>
                  <div className="w-full text-[0.875rem] font-primary font-regular text-darkgray md:mt-[23px]">
                    <FormFieldLabel label="Your Written Review *" />
                    <textarea
                      name="desc"
                      className="rectangle-textarea pl-[0.25rem] w-full"
                      value={note || ''}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <p className="text-darkgray font-primary text-[13px] leading-[15px]">Minimum of 10 characters</p>
                  </div>

                  <div className="w-full text-[0.875rem] font-primary font-regular text-darkgray mt-[23px]">
                    <CheckBox
                      labelClassName="i-have-read-and-acce"
                      label={'Anonymous Review(job title and type of company will be shown)'}
                      onChanged={onAnonymousChanged}
                      selected={isAnonymous}
                      indeterminate={isAnonymous}
                    />
                  </div>
                </div>

                <div className="flex flex-row mt-[21px] mb-[67px]">
                  <button
                    className="h-[50px] w-[152px] rounded-[25px] bg-base text-[#FFFFFF] font-secondary text-[14px] font-bold mr-[10px]"
                    onClick={handleSaveChanges}
                    disabled={isProgressing}
                  >
                    Submit Review
                  </button>
                  <button
                    className="h-[50px] w-[101px] rounded-[25px] bg-white text-darkgray font-secondary font-[500] text-[14px]"
                    style={{ border: '1px solid #DDDDDD' }}
                    disabled={isProgressing}
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            {isProgressing && (
              <div className="flex w-full h-full items-center justify-center absolute top-0 left-0">
                <CircularProgress />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
